import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Session } from '@supabase/supabase-js';

import { supabaseClient } from 'src/auth/supabase';
import { conciergeClient, ConciergeErrorResponse, ConciergeResponseCode } from 'src/api/concierge';
import { appConfig } from 'src/app/app-config';
import { getTheme } from 'src/utils/get-theme';

import { AuthStateEvent } from '../enum/AuthStateEvent';

import { useInitializeSupabase } from './use-initialise-supabase';

export const useLoginSupabase = () => {
    const [isAuthorized, setIsAuthorized] = useState(false);
    const ACCESS_TOKEN_KEY = appConfig.supabase.cookies.accessTokenKey as string;
    const REFRESH_TOKEN_KEY = appConfig.supabase.cookies.refreshTokenKey as string;
    const VS_COOKIE_DOMAIN = appConfig.supabase.cookies.domain;
    const HOS_COOKIE_DOMAIN = appConfig.supabase.cookies.hosDomain;

    const { isHosTheme } = getTheme();
    const cookieDomain = isHosTheme ? HOS_COOKIE_DOMAIN : VS_COOKIE_DOMAIN;

    const setSessionCookies = (session: Session | null) => {
        Cookies.set(ACCESS_TOKEN_KEY, session?.access_token ?? '', {
            domain: cookieDomain,
            secure: true,
            sameSite: 'Lax',
            expires: 7,
        });
        Cookies.set(REFRESH_TOKEN_KEY, session?.refresh_token ?? '', {
            domain: cookieDomain,
            secure: true,
            sameSite: 'Lax',
            expires: 7,
        });
    };

    const removeSessionCookies = () => {
        Cookies.remove(ACCESS_TOKEN_KEY, { domain: cookieDomain });
        Cookies.remove(REFRESH_TOKEN_KEY, { domain: cookieDomain });
    };

    const { mutateAsync: initializeSupabase } = useInitializeSupabase({
        onSuccess: (result) => setIsAuthorized(result),
    });

    useEffect(() => {
        initializeSupabase();

        const { data: authListener } = supabaseClient.auth.onAuthStateChange(
            async (_event, session) => {
                switch (_event) {
                    case AuthStateEvent.INITIAL_SESSION: {
                        const accessToken = Cookies.get(ACCESS_TOKEN_KEY);
                        const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
                        let isValidToken = true;

                        const { error } = await supabaseClient.auth.getUser(accessToken);

                        if (error && error.status === 403) {
                            isValidToken = false;
                        }

                        if (accessToken && refreshToken && isValidToken) {
                            supabaseClient.auth.setSession({
                                access_token: accessToken,
                                refresh_token: refreshToken,
                            });
                        }
                        break;
                    }
                    case AuthStateEvent.SIGNED_IN:
                        setSessionCookies(session);
                        break;
                    case AuthStateEvent.SIGNED_OUT:
                        removeSessionCookies();
                        break;
                    default:
                        break;
                }
                if (session?.access_token) {
                    conciergeClient.defaults.headers.common.Authorization = `Bearer ${session.access_token}`;
                    setIsAuthorized(true);
                } else {
                    setIsAuthorized(false);
                }
            }
        );

        return () => authListener?.subscription.unsubscribe();
    }, []);

    useEffect(() => {
        conciergeClient.interceptors.response.use(
            undefined,
            (error: AxiosError<ConciergeErrorResponse>) => {
                const errorCode = error.response?.data?.code;
                const host = error.request.responseURL;

                const isNotifications = host.includes(appConfig.concierge.notifications.baseUrl);

                if (errorCode === ConciergeResponseCode.TokenProblem && !isNotifications) {
                    supabaseClient.auth.signOut();
                }

                return Promise.reject(error);
            }
        );
    }, []);

    return isAuthorized;
};
